import * as React from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type ColumnFiltersState,
  type RowSelectionState,
} from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { LucideIcon } from './LucideIcon';
import { Text } from './Text';
import { Switch } from './Switch';
import { Icon } from './Icon';
import { Code } from './Code';
import { Color } from './Color';
import { Pdf } from './Pdf';
import { parseGeopoint, initLeafletIcon, calculateBounds, DEFAULT_CENTER, DEFAULT_ZOOM } from '../shared/geopoint';
import { isFileMetadata } from '../shared/image';
import type { List as Interface, Component, Switch as SwitchProps, Icon as IconProps, Code as CodeProps, Text as TextProps, Color as ColorProps, Pdf as PdfProps } from '../../design/components';
import type { Property } from '../../design/types';

initLeafletIcon();

const renderCellValue = (
  property: Property,
  component?: Component
): React.ReactNode => {
  if (!component) return '';

  const value = component.state?.[0];
  if (value === null || value === undefined) return '';

  switch (property.type) {
    case 'boolean':
      return <Switch {...component as SwitchProps} inline readonly />;
    case 'icon':
      return <Icon {...component as IconProps} inline readonly />;
    case 'code':
      return <Code {...component as CodeProps} inline readonly />;
    case 'color':
      return <Color {...component as ColorProps} inline readonly />;
    case 'pdf':
      return <Pdf {...component as PdfProps} inline readonly />;
    case 'reference':
    case 'object':
      return <List {...component as Interface} properties={property.reference?.properties || property.properties || []} inline readonly />;
    case 'custom':
      return null;
    default:
      return <Text {...component as TextProps} inline readonly />;
  }
};

interface MarkerData {
  rowIndex: number;
  coords: [number, number];
  components: Component[];
}

function FitBounds({ markers }: { markers: MarkerData[] }) {
  const map = useMap();

  React.useEffect(() => {
    if (markers.length === 0) return;

    const bounds = calculateBounds(markers.map(m => m.coords));
    if (bounds) {
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 });
    }
  }, [markers, map]);

  return null;
}

type ViewMode = 'table' | 'map' | 'card';

const getRowId = (rowComponents: Component[]): string => {
  const idComponent = rowComponents.find(c => c.name === 'id');
  return String(idComponent?.state?.[0] ?? '');
};

// Row data type for TanStack Table
interface RowData {
  _index: number;
  _id: string;
  _components: Component[];
  [key: string]: unknown;
}

export const List = ({
  name,
  description,
  properties,
  state,
  hidden,
  inline,
  label,
  disabled,
  readonly,
  onRowClick,
  options,
  limits,
  action,
  view,
}: Interface): React.ReactElement | null => {
  const [selectedRows, setSelectedRows] = state || [[], () => {}];
  const rows = options || selectedRows;
  const visibleProperties = properties.filter(p => !p.hidden);

  const [limitError, setLimitError] = React.useState<string | null>(null);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = React.useState('');

  const isEditMode = !!options && !readonly;
  const [min, max] = limits || [0, Infinity];

  // Convert selectedRows (Component[][]) to TanStack's RowSelectionState (Record<string, boolean>)
  const rowSelection = React.useMemo<RowSelectionState>(() => {
    const selection: RowSelectionState = {};
    for (const row of selectedRows) {
      selection[getRowId(row)] = true;
    }
    return selection;
  }, [selectedRows]);

  // Handle TanStack selection changes and sync back to component state
  const handleRowSelectionChange = React.useCallback(
    (updaterOrValue: RowSelectionState | ((old: RowSelectionState) => RowSelectionState)) => {
      const newSelection = typeof updaterOrValue === 'function'
        ? updaterOrValue(rowSelection)
        : updaterOrValue;

      const selectedRowIds = Object.keys(newSelection).filter(id => newSelection[id]);

      // Enforce limits
      if (max !== Infinity && selectedRowIds.length > max) {
        setLimitError(`Select at most ${max} item${max > 1 ? 's' : ''}`);
        return;
      }
      setLimitError(null);

      // Convert back to Component[][] format
      const newSelectedRows = selectedRowIds
        .map(id => rows.find(row => getRowId(row) === id))
        .filter((row): row is Component[] => row !== undefined);

      // Handle single selection mode
      if (max === 1) {
        const setValue = setSelectedRows as unknown as (v: Component[] | null) => void;
        setValue(newSelectedRows[0] || null);
        if (newSelectedRows.length === 1 && action) action[1]();
      } else {
        setSelectedRows(newSelectedRows);
        if (max !== Infinity && newSelectedRows.length >= max && action) action[1]();
      }
    },
    [rowSelection, rows, max, action, setSelectedRows]
  );

  // Keep selectedIds for map/card views that don't use TanStack
  const selectedIds = React.useMemo(
    () => new Set(selectedRows.map(row => getRowId(row))),
    [selectedRows]
  );

  // Toggle for map/card views
  const toggleItem = (rowIndex: number) => {
    const rowId = getRowId(rows[rowIndex]);
    const newSelection = { ...rowSelection };
    if (newSelection[rowId]) {
      delete newSelection[rowId];
    } else {
      newSelection[rowId] = true;
    }
    handleRowSelectionChange(newSelection);
  };

  const [viewState, setViewState] = React.useState<{ mode: ViewMode; modelName: string | undefined }>({
    mode: view || 'table',
    modelName: name,
  });

  const viewMode = viewState.modelName === name ? viewState.mode : (view || 'table');
  const setViewMode = (mode: ViewMode) => setViewState({ mode, modelName: name });

  const geopointProp = React.useMemo(
    () => properties.find(p => p.type === 'geopoint'),
    [properties]
  );

  const imageProp = React.useMemo(
    () => properties.find(p => p.type === 'image'),
    [properties]
  );

  const hasAlternateViews = geopointProp || imageProp;

  const markersData = React.useMemo(() => {
    if (!geopointProp) return [];
    return rows
      .map((rowComponents, rowIndex) => {
        const geopointComponent = rowComponents.find(c => c.name === geopointProp.name);
        const value = geopointComponent?.state?.[0] as string | undefined;
        const coords = parseGeopoint(value);
        return coords ? { rowIndex, coords, components: rowComponents } : null;
      })
      .filter((m): m is MarkerData => m !== null);
  }, [rows, geopointProp]);

  const handleRowClick = (rowIndex: number) => {
    if (disabled) return;
    if (isEditMode) {
      toggleItem(rowIndex);
    } else {
      onRowClick?.(rowIndex);
    }
  };

  const handleActionClick = () => {
    if (!action) return;
    if (isEditMode && min > 0 && selectedRows.length < min) {
      setLimitError(`Select at least ${min} item${min > 1 ? 's' : ''}`);
      return;
    }
    setLimitError(null);
    action[1]();
  };

  // Convert Component[][] to RowData[] for TanStack Table
  const tableData = React.useMemo<RowData[]>(() => {
    return rows.map((rowComponents, index) => {
      const rowData: RowData = {
        _index: index,
        _id: getRowId(rowComponents) || `row-${index}`,
        _components: rowComponents,
      };
      // Add each property value for sorting/filtering
      // Use label (display text) for reference/object types, raw value otherwise
      for (const prop of properties) {
        const component = rowComponents.find(c => c.name === prop.name);
        if ((prop.type === 'reference' || prop.type === 'object') && component?.label != null) {
          rowData[prop.name] = String(component.label);
        } else {
          rowData[prop.name] = component?.state?.[0] ?? '';
        }
      }
      return rowData;
    });
  }, [rows, properties]);

  // Generate columns dynamically from properties
  const columns = React.useMemo<ColumnDef<RowData>[]>(() => {
    const cols: ColumnDef<RowData>[] = [];

    // Checkbox column for edit mode
    if (isEditMode) {
      cols.push({
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && 'indeterminate')
            }
            onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
            onClick={(e) => e.stopPropagation()}
          />
        ),
        enableSorting: false,
        enableHiding: false,
        size: 40,
      });
    }

    // Property columns
    for (const property of visibleProperties) {
      cols.push({
        accessorKey: property.name,
        header: ({ column }) => (
          <Button
            variant="ghost"
            size="sm"
            className="-ml-3 h-8 data-[state=open]:bg-accent group"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            <span className="flex items-center gap-2">
              {property.name}
              {column.getIsSorted() === 'asc' ? (
                <LucideIcon name="ArrowUp" size="small" />
              ) : column.getIsSorted() === 'desc' ? (
                <LucideIcon name="ArrowDown" size="small" />
              ) : (
                <LucideIcon name="ArrowUpDown" size="small" className="opacity-0 group-hover:opacity-100 transition-opacity" />
              )}
            </span>
          </Button>
        ),
        cell: ({ row }) => {
          const component = row.original._components.find(c => c.name === property.name);
          return renderCellValue(property, component);
        },
      });
    }

    return cols;
  }, [visibleProperties, isEditMode]);

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onRowSelectionChange: handleRowSelectionChange,
    enableRowSelection: isEditMode,
    getRowId: (row) => row._id,
    state: {
      sorting,
      columnFilters,
      globalFilter,
      rowSelection,
    },
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
  });

  if (inline) {
    return <span>{label || ''}</span>;
  }

  const renderToolbar = () => {
    return (
      <div className={cn("flex items-center justify-between gap-4", name ? "py-4" : "pb-2")}>
        <Input
          placeholder="Filter..."
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          className="max-w-sm h-8"
        />
        <div className="flex items-center gap-2">
          {limitError && (
            <span className="text-sm text-destructive">{limitError}</span>
          )}
          {action && (
            <Button variant="outline" size="sm" onClick={handleActionClick}>
              {action[0]}
            </Button>
          )}
          {hasAlternateViews && (
            <>
              <Button
                variant={viewMode === 'table' ? 'secondary' : 'ghost'}
                size="icon-sm"
                onClick={() => setViewMode('table')}
                title="Table view"
              >
                <LucideIcon name="Table" size="small" />
              </Button>
              {imageProp && (
                <Button
                  variant={viewMode === 'card' ? 'secondary' : 'ghost'}
                  size="icon-sm"
                  onClick={() => setViewMode('card')}
                  title="Card view"
                >
                  <LucideIcon name="LayoutGrid" size="small" />
                </Button>
              )}
              {geopointProp && (
                <Button
                  variant={viewMode === 'map' ? 'secondary' : 'ghost'}
                  size="icon-sm"
                  onClick={() => setViewMode('map')}
                  title="Map view"
                >
                  <LucideIcon name="Map" size="small" />
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  const renderTable = () => (
    <>
      <div className="overflow-hidden rounded-md border">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id} className="hover:bg-transparent">
              {headerGroup.headers.map((header, index) => (
                <TableHead
                  key={header.id}
                  className={cn(
                    index === 0 && 'pl-4',
                    index === headerGroup.headers.length - 1 && 'pr-4'
                  )}
                  style={{ width: header.getSize() !== 150 ? header.getSize() : undefined }}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className="h-24 text-center text-muted-foreground"
              >
                No results.
              </TableCell>
            </TableRow>
          ) : (
            table.getRowModel().rows.map((row) => {
              return (
                <TableRow
                  key={row.id}
                  onClick={() => handleRowClick(row.original._index)}
                  data-state={row.getIsSelected() ? 'selected' : undefined}
                  className={cn(
                    '!bg-background hover:!bg-muted/50',
                    !disabled && 'cursor-pointer',
                    isEditMode && row.getIsSelected() && '!bg-primary/10'
                  )}
                >
                  {row.getVisibleCells().map((cell, index) => (
                    <TableCell
                      key={cell.id}
                      className={cn(
                        index === 0 && 'pl-4',
                        index === row.getVisibleCells().length - 1 && 'pr-4'
                      )}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
      </div>
      {/* Pagination — only show when there are more rows than one page */}
      {(table.getCanNextPage() || table.getCanPreviousPage() || isEditMode) && (
        <div className={cn("flex items-center justify-between", name ? "py-4" : "pt-2")}>
          <div className="text-sm text-muted-foreground">
            {isEditMode ? (
              `${table.getFilteredSelectedRowModel().rows.length} of ${table.getFilteredRowModel().rows.length} row(s) selected.`
            ) : (
              `${table.getFilteredRowModel().rows.length} row(s) total.`
            )}
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </>
  );

  const renderMap = () => {
    const popupProperties = visibleProperties.filter(
      p => p.type !== 'image' && p.type !== 'geopoint'
    );

    return (
      <div className="h-[400px] w-full overflow-hidden rounded-md border">
        <MapContainer
          center={markersData.length > 0 ? markersData[0].coords : DEFAULT_CENTER}
          zoom={DEFAULT_ZOOM}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {markersData.map(marker => {
            const rowId = getRowId(marker.components);
            const isSelected = selectedIds.has(rowId);
            return (
              <Marker
                key={rowId || `marker-${marker.rowIndex}`}
                position={marker.coords}
                opacity={isEditMode && !isSelected ? 0.5 : 1}
              >
                <Popup>
                  <div className="min-w-[150px]">
                    {isEditMode && (
                      <div className="flex items-center mb-2 gap-2">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleItem(marker.rowIndex)}
                        />
                        <span className="text-sm text-muted-foreground">
                          {isSelected ? 'Selected' : 'Not selected'}
                        </span>
                      </div>
                    )}
                    {popupProperties.slice(0, 4).map((property, index) => {
                      const component = marker.components.find((c: Component) => c.name === property.name);
                      const value = renderCellValue(property, component);
                      return (
                        <p
                          key={property.name}
                          className={cn(
                            index === 0 ? 'font-semibold' : 'text-muted-foreground text-sm'
                          )}
                        >
                          {value}
                        </p>
                      );
                    })}
                    <button
                      type="button"
                      onClick={() => isEditMode ? toggleItem(marker.rowIndex) : onRowClick?.(marker.rowIndex)}
                      className="mt-2 text-sm text-primary hover:underline bg-transparent border-none p-0 cursor-pointer"
                    >
                      {isEditMode ? (isSelected ? 'Deselect' : 'Select') : 'View details'}
                    </button>
                  </div>
                </Popup>
              </Marker>
            );
          })}
          <FitBounds markers={markersData} />
        </MapContainer>
      </div>
    );
  };

  const renderCards = () => {
    const cardProperties = visibleProperties.filter(
      p => p.type !== 'image' && p.type !== 'geopoint'
    );

    if (rows.length === 0) {
      return (
        <div className="p-6 text-center text-muted-foreground">
          No data
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 pb-4">
        {rows.map((rowComponents, rowIndex) => {
          const rowId = getRowId(rowComponents);
          const imageComponent = imageProp
            ? rowComponents.find(c => c.name === imageProp.name)
            : undefined;
          const rawImage = imageComponent?.state?.[0];
          const imageUrl = isFileMetadata(rawImage) ? rawImage.url : undefined;
          const isSelected = selectedIds.has(rowId);

          return (
            <div
              key={rowId || `card-${rowIndex}`}
              onClick={() => handleRowClick(rowIndex)}
              className={cn(
                'relative rounded-md border bg-card overflow-hidden transition-colors',
                !disabled && 'cursor-pointer hover:bg-muted/50',
                isEditMode && isSelected && 'ring-2 ring-primary'
              )}
            >
              {isEditMode && (
                <div className="absolute top-2 left-2 z-10">
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => toggleItem(rowIndex)}
                  />
                </div>
              )}
              {imageUrl && (
                <div className="h-40 overflow-hidden">
                  <img
                    src={imageUrl}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <div className="p-3">
                {cardProperties.slice(0, 4).map((property, index) => {
                  const component = rowComponents.find((c: Component) => c.name === property.name);
                  const value = renderCellValue(property, component);
                  return (
                    <p
                      key={property.name}
                      className={cn(
                        'truncate',
                        index === 0 ? 'font-semibold' : 'text-sm text-muted-foreground'
                      )}
                    >
                      {value}
                    </p>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderContent = () => {
    switch (viewMode) {
      case 'map':
        return renderMap();
      case 'card':
        return renderCards();
      default:
        return renderTable();
    }
  };

  return (
    <div
      className={cn(
        'bg-background',
        hidden && 'hidden',
        disabled && 'opacity-60'
      )}
    >
      {name && (
        <div className="pb-4">
          <h2 className="text-2xl font-semibold tracking-tight">{name}</h2>
          {description && (
            <p className="text-sm text-muted-foreground">{description}</p>
          )}
        </div>
      )}
      {renderToolbar()}
      {renderContent()}
    </div>
  );
};
